import { Button, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { useFormik } from "formik";
import React from "react";
import { BiPlus } from "react-icons/bi";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { addDailyTask } from "../../../../adapters";
import EmojiPickerModal from "../../../../components/EmojiPickerModal";
import Loader from "../../../../components/Loader";
import GModal from "../../../../components/Modal";
import { addTaskToList } from "../../../../config/redux/slices/schedulerSlice";
import { showErrors } from "../../../../utils/ui";
import { StyledAdditionForm, StyledEmojiRow } from "./styles";
import { validateCronJobExpression } from "../../../../utils/validations";
import CronJobInput from "../../../Dialer/components/CronJobInput";

const TaskAdditionModal = ({ visible, setVisible }) => {
  const dispatch = useDispatch();
  const [processing, setIsProcessing] = React.useState(false);
  const closeModal = () => {
    !processing && setVisible(false);
  };

  const {
    handleSubmit,
    handleBlur,
    handleChange,
    values,
    errors,
    touched,
    setFieldTouched,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues: {
      title: "",
      description: "",
      emoji: "",
      enableNotifications: false,
      notifyOn: "0 10 * * *",
      notificationMessage: "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Enter task title"),
      description: Yup.string().required("Enter description"),
      emoji: Yup.string()
        .required("Select Emoji")
        .min(1, "Select one emoji")
        .max(10, "Select one emoji only"),
      notifyOn: Yup.string()
        .test(
          "expression-validation",
          "Enter valid linux expression",
          (value, context) =>
            !context.parent.enableNotifications ||
            validateCronJobExpression(value)
        )
        .required("Expression is required"),
      notificationMessage: Yup.string().optional(),
    }),
    onSubmit: async (values) => {
      setIsProcessing(true);
      try {
        const response = await addDailyTask({
          title: values.title,
          description: values.description,
          emoji: values.emoji,
          enableNotifications: values.enableNotifications,
          notifyOn: values.notifyOn,
          notificationMessage: values.notificationMessage,
        });
        toast.success(response.message);
        dispatch(addTaskToList(response.data));
        setVisible(false);
        resetForm();
      } catch (e) {
        showErrors(e);
      }
      setIsProcessing(false);
    },
  });

  return (
    <GModal
      isOpen={visible}
      onClose={closeModal}
      title="Daily Task Addition"
      width="540px"
    >
      {processing && <Loader absolute showText="Adding task.." />}
      <StyledAdditionForm onSubmit={handleSubmit}>
        <TextField
          value={values.title}
          type="text"
          name="title"
          label="Title"
          placeholder="Enter your title"
          error={touched.title && !!errors.title}
          helperText={touched.title ? errors.title : ""}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <TextField
          value={values.description}
          type="text"
          name="description"
          label="Description"
          placeholder="Enter your description"
          error={touched.description && !!errors.description}
          helperText={touched.description ? errors.description : ""}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={values.enableNotifications}
              onChange={(_, checked) =>
                setFieldValue("enableNotifications", checked)
              }
            />
          }
          label="Enable Notifications"
        />
        {values.enableNotifications && (
          <CronJobInput
            value={values.notifyOn ?? ""}
            error={errors.notifyOn}
            setValue={(value) => setFieldValue("notifyOn", value)}
            onBlur={() => setFieldTouched("notifyOn", true)}
          />
        )}
        {values.enableNotifications && (
          <TextField
            value={values.notificationMessage}
            type="text"
            name="notificationMessage"
            label="Notification Message"
            placeholder="Enter your message"
            onChange={handleChange}
            onBlur={handleBlur}
            margin="dense"
          />
        )}
        <StyledEmojiRow>
          <EmojiPickerModal
            onEmojiSelect={(emoji) => setFieldValue("emoji", emoji)}
            onTouch={() => setFieldTouched("emoji", true)}
            value={values.emoji}
            error={touched.emoji ? errors.emoji : ""}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            endIcon={<BiPlus />}
          >
            Add
          </Button>
        </StyledEmojiRow>
      </StyledAdditionForm>
    </GModal>
  );
};

export default TaskAdditionModal;
